import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit2, Trash2, Image as ImageIcon, Package, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ProductForm from '../components/ProductForm';
import { EmptyState } from '../components/ui/empty-state';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsPage = () => {
  const { t } = useTranslation('products');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // TODO: Get workspace_id from context/auth
  const workspaceId = 'ws_demo';

  useEffect(() => {
    fetchProducts();
  }, [searchTerm, statusFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/products?${params}`,
        { credentials: 'include' }  // Send cookies for authentication
      );
      
      if (!response.ok) {
        console.error(`Failed to fetch products: HTTP ${response.status}`);
        setProducts([]);
        return;
      }
      
      const data = await response.json();
      console.log('✅ Fetched products:', {
        count: data.products?.length || 0,
        total: data.total,
        firstProduct: data.products?.[0]?.name
      });
      setProducts(data.products || []);
    } catch (error) {
      console.error('❌ Failed to fetch products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm(t('confirm_delete'))) return;

    try {
      await fetch(
        `${API_URL}/api/v1/workspaces/${workspaceId}/products/${productId}`,
        { 
          method: 'DELETE',
          credentials: 'include'  // Send cookies for authentication
        }
      );
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingProduct(null);
  };

  const handleProductSaved = () => {
    handleCloseModal();
    fetchProducts();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 truncate">{t('title')}</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">{t('subtitle')}</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 w-full sm:w-auto justify-center"
            data-testid="create-product-btn"
          >
            <Plus size={20} />
            <span className="sm:inline">{t('button_new')}</span>
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6" data-testid="products-filters">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  placeholder={t('search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="product-search-input"
                />
              </div>

              {/* Status Filter */}
              <div className="w-full md:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-md bg-white"
                  data-testid="product-status-filter"
                >
                  <option value="all">{t('filter_all_statuses')}</option>
                  <option value="active">{t('filter_active')}</option>
                  <option value="draft">{t('filter_draft')}</option>
                  <option value="archived">{t('filter_archived')}</option>
                  <option value="out_of_stock">{t('filter_out_of_stock')}</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20" data-testid="products-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            icon={<Package size={56} />}
            title="Créez votre catalogue produits 📦"
            message="Ajoutez vos produits pour générer automatiquement des descriptions, visuels et publications optimisées par IA."
            primaryAction={{
              label: "Ajouter mon premier produit",
              onClick: () => setShowCreateModal(true),
              icon: <Plus size={20} />
            }}
            secondaryAction={{
              label: "Importer un catalogue CSV",
              onClick: () => {
                // TODO: Implement CSV import
                alert('Fonctionnalité d\'import CSV à venir');
              },
              variant: "outline",
              icon: <Upload size={20} />
            }}
            variant="empty"
            size="large"
            animated={true}
            testId="empty-products"
            badge="Nouveau"
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="products-grid">
            {products.map((product) => (
              <Card key={product.id} className="hover:shadow-lg transition-shadow" data-testid={`product-card-${product.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start gap-2">
                    <CardTitle className="text-base sm:text-lg flex-1 min-w-0 truncate">{product.name}</CardTitle>
                    <Badge variant={product.status === 'active' ? 'default' : 'secondary'} className="shrink-0">
                      {product.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Product Image */}
                  {product.main_image || product.images?.[0] ? (
                    <div className="aspect-video bg-slate-100 rounded-md mb-3 overflow-hidden">
                      <img
                        src={
                          (product.main_image || product.images[0]).startsWith('http')
                            ? (product.main_image || product.images[0])
                            : `${API_URL}${product.main_image || product.images[0]}`
                        }
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50%" y="50%" fill="%23999" text-anchor="middle" dy=".3em">No Image</text></svg>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate-100 rounded-md mb-3 flex items-center justify-center">
                      <ImageIcon className="text-slate-300" size={32} />
                    </div>
                  )}

                  {/* Product Info */}
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {product.description || 'Aucune description'}
                  </p>

                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg sm:text-xl font-bold text-slate-900">
                      {product.price} {product.currency}
                    </span>
                    {product.stock > 0 ? (
                      <span className="text-xs sm:text-sm text-green-600">Stock: {product.stock}</span>
                    ) : (
                      <span className="text-xs sm:text-sm text-red-600">Rupture</span>
                    )}
                  </div>

                  {/* Tags */}
                  {product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {product.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs sm:text-sm"
                      onClick={() => handleEditProduct(product)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit2 size={16} className="mr-1" />
                      <span className="hidden sm:inline">Modifier</span>
                      <span className="sm:hidden">Edit</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteProduct(product.id)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
            data-testid="create-product-modal"
          >
            <div 
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <ProductForm
                product={editingProduct}
                onSuccess={handleProductSaved}
                onCancel={handleCloseModal}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
